import { useState } from "react";
import { Checkbox, Select, Tag } from "antd";
import { BrandKitDto } from "../../../../../generated/dashboard-client";
import "./BrandKitMultiSelect.scss";

const ALL_VALUE = "__all__";

export const kitMeta = (kit: BrandKitDto) => {
  const locations = kit.locationIds?.length ?? 0;
  const templates = kit.templateCount ?? 0;
  return `${locations} location${locations === 1 ? "" : "s"} • ${templates} template${templates === 1 ? "" : "s"}`;
};

interface BrandKitMultiSelectProps {
  kits: BrandKitDto[];
  selected: string[];
  onChange: (ids: string[]) => void;
  getMeta?: (kit: BrandKitDto) => string | undefined;
  isDisabled?: (kit: BrandKitDto) => boolean;
}

export default function BrandKitMultiSelect({
  kits,
  selected,
  onChange,
  getMeta = kitMeta,
  isDisabled = () => false,
}: BrandKitMultiSelectProps) {
  const [search, setSearch] = useState("");
  const showHint = selected.length > 0 && !search;
  const selectableIds = kits.filter((b) => !isDisabled(b)).map((b) => b.id!);
  const allSelected = selectableIds.length > 0 && selected.length === selectableIds.length;

  function handleChange(values: string[]) {
    if (values.includes(ALL_VALUE)) {
      onChange(allSelected ? [] : selectableIds);
      return;
    }
    onChange(values);
  }

  const tagRender = (props: any) => {
    if (allSelected) {
      if (props.value !== selected[0]) return <span style={{ display: "none" }} />;
      return (
        <Tag
          closable
          onClose={(e: any) => {
            e.preventDefault();
            onChange([]);
          }}
          style={{ marginRight: 3 }}
        >
          All Brand Kits
        </Tag>
      );
    }
    return (
      <Tag
        closable
        onClose={(e: any) => {
          e.preventDefault();
          props.onClose();
        }}
        style={{ marginRight: 3 }}
      >
        {props.label}
      </Tag>
    );
  };

  return (
    <Select
      mode="multiple"
      className={`assign-brand-select brand-kit-multi-select${showHint ? " has-selection" : ""}`}
      size="large"
      style={{ width: "100%" }}
      placeholder="Select or type to search"
      value={selected}
      onChange={(vals) => handleChange(vals as string[])}
      optionFilterProp="label"
      optionLabelProp="label"
      maxTagCount="responsive"
      tagRender={tagRender}
      menuItemSelectedIcon={null}
      dropdownClassName="assign-brand-dropdown"
      autoClearSearchValue={false}
      onSearch={setSearch}
      onBlur={() => setSearch("")}
      notFoundContent="No matching Brand Kits"
    >
      {selectableIds.length > 1 && (
        <Select.Option value={ALL_VALUE} label="All Brand Kits" className="assign-brand-option">
          <Checkbox checked={allSelected} />
          <span>All Brand Kits</span>
        </Select.Option>
      )}
      {kits.map((b) => {
        const disabled = isDisabled(b);
        return (
          <Select.Option
            key={b.id}
            value={b.id!}
            label={b.name || "Untitled"}
            className="assign-brand-option"
            disabled={disabled}
          >
            <Checkbox checked={disabled || selected.includes(b.id!)} disabled={disabled} />
            <span>{b.name || "Untitled"}</span>
            <span style={{ color: "#999", fontSize: 12 }}>{getMeta(b)}</span>
          </Select.Option>
        );
      })}
    </Select>
  );
}
